import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Power, Search, Loader2, Bus, Bath, DollarSign, UploadCloud, BookOpen, Tag } from "lucide-react";
import TourFormDialog from "@/pages/config/TourFormDialog";
import TarifaTourDialog from "@/pages/config/TarifaTourDialog";
import FichaComercialDialog from "@/pages/config/FichaComercialDialog";
import NombresProductoDialog from "@/pages/config/NombresProductoDialog";
import ImportarTarifasDialog from "@/components/config/ImportarTarifasDialog";
import { CATEGORIAS, TIPOS_MODALIDAD, VEHICULOS, diasResumen } from "@/lib/constants";

function ModalidadChips({ modalidades }) {
  return (
    <div className="flex flex-wrap gap-1">
      {modalidades.map((m, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border bg-slate-50 text-slate-600 border-slate-200"
        >
          {TIPOS_MODALIDAD[m.tipo]} · {VEHICULOS[m.vehiculo]} {m.cupo_max}
        </span>
      ))}
    </div>
  );
}

export default function ToursConfig() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoria, setCategoria] = useState("todas");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmTour, setConfirmTour] = useState(null);
  const [tarifasOpen, setTarifasOpen] = useState(false);
  const [tarifasTour, setTarifasTour] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const abrirTarifas = (tour) => { setTarifasTour(tour); setTarifasOpen(true); };
  // PROMPT 88 (2026-09-01): ficha comercial (lo que lee el agente de WhatsApp).
  const [fichaOpen, setFichaOpen] = useState(false);
  const [fichaTour, setFichaTour] = useState(null);
  const abrirFicha = (tour) => { setFichaTour(tour); setFichaOpen(true); };
  // PROMPT 116: nombres de producto con los que los correos reconocen el tour.
  const [nombresOpen, setNombresOpen] = useState(false);
  const [nombresTour, setNombresTour] = useState(null);
  const abrirNombres = (tour) => { setNombresTour(tour); setNombresOpen(true); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoria !== "todas") params.categoria = categoria;
      if (q) params.q = q;
      const { data } = await api.get("/tours", { params });
      setTours(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [categoria, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (tour) => {
    setEditing(tour);
    setDialogOpen(true);
  };

  const toggleActivo = async (tour) => {
    try {
      if (tour.activo) {
        await api.delete(`/tours/${tour.id}`);
        toast.success("Tour desactivado.");
      } else {
        await api.put(`/tours/${tour.id}`, { ...tour, activo: true });
        toast.success("Tour reactivado.");
      }
      setConfirmTour(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar tour..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="tours-search-input"
            />
          </div>
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger className="w-full sm:w-52" data-testid="tours-categoria-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las categorías</SelectItem>
              {Object.entries(CATEGORIAS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="importar-tarifas-tour-button" className="gap-2">
            <UploadCloud className="h-4 w-4" /> Importar tarifas (archivo)
          </Button>
          <Button onClick={openNew} data-testid="new-tour-button" className="gap-2">
            <Plus className="h-4 w-4" /> Nuevo tour
          </Button>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Tour</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Categoría</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Modalidades</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Horarios</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Estado</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" />
                  </TableCell>
                </TableRow>
              ) : tours.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center text-slate-400 text-sm">
                    No se encontraron tours.
                  </TableCell>
                </TableRow>
              ) : (
                tours.map((tour) => {
                  const horas = [
                    ...new Set(tour.modalidades.flatMap((m) => m.horarios.map((h) => h.hora))),
                  ].sort();
                  const dias = tour.modalidades[0]?.horarios[0]?.dias_semana || [];
                  return (
                    <TableRow key={tour.id} data-testid={`tour-row-${tour.id}`} className="hover:bg-slate-50">
                      <TableCell className="font-medium text-slate-900">
                        <div className="flex items-center gap-2">
                          {tour.categoria === "privado" ? (
                            <Bath className="h-4 w-4 text-slate-400" />
                          ) : (
                            <Bus className="h-4 w-4 text-slate-400" />
                          )}
                          <div>
                            <div>{tour.nombre}</div>
                            <div className="text-[11px] text-slate-400 uppercase tracking-wide">
                              {tour.idiomas.join(" / ")}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{CATEGORIAS[tour.categoria]}</TableCell>
                      <TableCell>
                        <ModalidadChips modalidades={tour.modalidades} />
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="font-mono text-slate-700">{horas.join(" · ") || "—"}</div>
                        <div className="text-[11px] text-slate-400">{diasResumen(dias)}</div>
                      </TableCell>
                      <TableCell>
                        {tour.activo ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-slate-100 text-slate-500 border-slate-200">
                            Inactivo
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => abrirFicha(tour)}
                            data-testid={`ficha-tour-${tour.id}`}
                            className={`h-8 w-8 ${tour.ficha_comercial?.es?.descripcion ? "text-sky-600" : "text-slate-400"} hover:text-sky-700`}
                            title="Ficha comercial (agente de WhatsApp)"
                          >
                            <BookOpen className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => abrirNombres(tour)}
                            data-testid={`nombres-tour-${tour.id}`}
                            className={`h-8 w-8 ${(tour.alias_producto || []).length ? "text-violet-600" : "text-slate-400"} hover:text-violet-700`}
                            title="Nombres en los correos"
                          >
                            <Tag className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => abrirTarifas(tour)}
                            data-testid={`tarifas-tour-${tour.id}`}
                            className="h-8 w-8 text-slate-500 hover:text-emerald-600"
                            title="Tarifas base"
                          >
                            <DollarSign className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(tour)}
                            data-testid={`edit-tour-${tour.id}`}
                            className="h-8 w-8 text-slate-500 hover:text-slate-900"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmTour(tour)}
                            data-testid={`toggle-tour-${tour.id}`}
                            className={`h-8 w-8 ${tour.activo ? "text-slate-500 hover:text-red-600" : "text-slate-500 hover:text-emerald-600"}`}
                          >
                            <Power className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <p className="text-xs text-slate-400">{tours.length} tour(s) en el catálogo.</p>

      <TourFormDialog open={dialogOpen} onOpenChange={setDialogOpen} tour={editing} onSaved={load} />
      <TarifaTourDialog open={tarifasOpen} onOpenChange={setTarifasOpen} tour={tarifasTour} onSaved={load} />
      <FichaComercialDialog open={fichaOpen} onOpenChange={setFichaOpen} tour={fichaTour} onSaved={load} />
      <NombresProductoDialog open={nombresOpen} onOpenChange={setNombresOpen} tour={nombresTour} onSaved={load} />
      <ImportarTarifasDialog open={importOpen} onOpenChange={setImportOpen} tipo="tour" onImported={load} />

      <AlertDialog open={!!confirmTour} onOpenChange={(o) => !o && setConfirmTour(null)}>
        <AlertDialogContent data-testid="toggle-tour-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTour?.activo ? "Desactivar tour" : "Reactivar tour"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTour?.activo
                ? `"${confirmTour?.nombre}" dejará de estar disponible para nuevas reservas. Podés reactivarlo cuando quieras.`
                : `"${confirmTour?.nombre}" volverá a estar disponible.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => toggleActivo(confirmTour)} data-testid="confirm-toggle-tour">
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
